import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaUsers,
  FaBoxOpen,
  FaShoppingCart,
  FaEnvelope,
  FaLink,
  FaFileInvoice,
  FaUndoAlt,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { getCsrfToken } from "../../services/authService";
import "../../styles/Admin.css";

export default function AdminDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [catalogs, setCatalogs] = useState([]);
  const [catalogTitle, setCatalogTitle] = useState("");
  const [catalogFile, setCatalogFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(Date.now());

  useEffect(() => {
    loadStats();
    loadCatalogs();
  }, []);

  const loadCatalogs = async () => {
    const response = await fetch("/api/admin/catalogs", {
      credentials: "include",
    });

    if (response.ok) {
      setCatalogs(await response.json());
    }
  };

  const uploadCatalog = async (e) => {
    e.preventDefault();

    if (!catalogFile) return;

    const csrf = await getCsrfToken();

    const formData = new FormData();
    formData.append("title", catalogTitle);
    formData.append("file", catalogFile);

    const response = await fetch("/api/admin/catalogs", {
      method: "POST",
      credentials: "include",
      headers: {
        [csrf.headerName]: csrf.token,
      },
      body: formData,
    });

    if (response.ok) {
      setCatalogTitle("");
      setCatalogFile(null);
      setFileInputKey(Date.now());
      await loadCatalogs();
    }
  };

  const deleteCatalog = async (id) => {
    const csrf = await getCsrfToken();

    const response = await fetch(`/api/admin/catalogs/${id}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        [csrf.headerName]: csrf.token,
      },
    });

    if (response.ok) {
      setCatalogs((prev) => prev.filter((catalog) => catalog.id !== id));
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch("/api/admin/dashboard", {
        credentials: "include",
      });

      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error("Error loading dashboard stats", error);
    }
  };

  return (
    <main className="admin-page">
      <section className="admin-container">
        <h1>{t("admin.title")}</h1>
        <p className="admin-subtitle">{t("admin.subtitle")}</p>

        <div className="admin-stats-grid">
          <div className="admin-stat-card">
            <span>{stats?.totalUsers ?? "-"}</span>
            <p>{t("admin.stats.users")}</p>
          </div>

          <div className="admin-stat-card">
            <span>{stats?.totalAdmins ?? "-"}</span>
            <p>{t("admin.stats.admins")}</p>
          </div>

          <div className="admin-stat-card">
            <span>{stats?.disabledUsers ?? "-"}</span>
            <p>{t("admin.stats.disabledUsers")}</p>
          </div>

          <div className="admin-stat-card">
            <span>{stats?.unreadMessages ?? "-"}</span>
            <p>{t("admin.stats.unreadMessages")}</p>
          </div>

          <div className="admin-stat-card">
            <span>{stats?.totalProducts ?? "-"}</span>
            <p>{t("admin.stats.products")}</p>
          </div>

          <div className="admin-stat-card">
            <span>{stats?.activeProducts ?? "-"}</span>
            <p>{t("admin.stats.activeProducts")}</p>
          </div>

          <div className="admin-stat-card">
            <span>{stats?.lowStockProducts ?? "-"}</span>
            <p>{t("admin.stats.lowStockProducts")}</p>
          </div>

          <div className="admin-stat-card">
            <span>{stats?.totalOrders ?? "-"}</span>
            <p>{t("admin.stats.orders")}</p>
          </div>

          <div className="admin-stat-card">
            <span>{stats?.pendingOrders ?? "-"}</span>
            <p>{t("admin.stats.pendingOrders")}</p>
          </div>

          <div className="admin-stat-card">
            <span>{stats?.shippedOrders ?? "-"}</span>
            <p>{t("admin.stats.shippedOrders")}</p>
          </div>

          <div className="admin-stat-card">
            <span>{stats?.deliveredOrders ?? "-"}</span>
            <p>{t("admin.stats.deliveredOrders")}</p>
          </div>

          <div className="admin-stat-card">
            <span>
              {stats?.totalSales !== undefined
                ? `${Number(stats.totalSales).toFixed(2)} €`
                : "-"}
            </span>
            <p>{t("admin.stats.totalSales")}</p>
          </div>
        </div>

        <div className="admin-grid">
          <article
            className="admin-card"
            onClick={() => navigate("/admin/users")}
          >
            <FaUsers className="admin-icon" />
            <h2>{t("admin.users")}</h2>
            <p>{t("admin.usersText")}</p>
          </article>

          <article 
            className="admin-card"
            onClick={() => navigate("/admin/orders")}
          >
            <FaShoppingCart className="admin-icon" />
            <h2>{t("admin.orders")}</h2>
            <p>{t("admin.ordersText")}</p>
          </article>

          <article
            className="admin-card"
            onClick={() => navigate("/admin/returns")}
          >
            <FaUndoAlt className="admin-icon" />
            <h2>{t("admin.returns")}</h2>
            <p>{t("admin.returnsText")}</p>
          </article>

          <article
            className="admin-card"
            onClick={() => navigate("/admin/products")}
          >
            <FaBoxOpen className="admin-icon" />
            <h2>{t("admin.products")}</h2>
            <p>{t("admin.productsText")}</p>
          </article>

          <article
            className="admin-card"
            onClick={() => navigate("/admin/compatibilities")}
          >
            <FaLink className="admin-icon" />
            <h2>{t("admin.compatibilities")}</h2>
            <p>{t("admin.compatibilitiesText")}</p>
          </article>

          <article
            className="admin-card"
            onClick={() => navigate("/admin/messages")}
          >
            <FaEnvelope className="admin-icon" />
            <h2>{t("admin.messages")}</h2>
            <p>{t("admin.messagesText")}</p>
          </article>

          <article
            className="admin-card"
            onClick={() => navigate("/admin/quotes")}
          >
            <FaFileInvoice className="admin-icon" />
            <h2>{t("admin.quotes")}</h2>
            <p>{t("admin.quotesText")}</p>
          </article>
        </div>

        <section className="admin-catalogs-section">
          <div className="admin-catalogs-header">
            <div>
              <h2>{t("admin.catalogs.title")}</h2>
              <p>{t("admin.catalogs.subtitle")}</p>
            </div>
          </div>

          <form className="admin-catalog-form" onSubmit={uploadCatalog}>
            <input
              type="text"
              placeholder={t("admin.catalogs.namePlaceholder")}
              value={catalogTitle}
              onChange={(e) => setCatalogTitle(e.target.value)}
              required
            />

            <input
              key={fileInputKey}
              type="file"
              accept="application/pdf"
              onChange={(e) => setCatalogFile(e.target.files[0])}
              required
            />

            <button type="submit">{t("admin.catalogs.upload")}</button>
          </form>

          <div className="admin-catalogs-list">
            {catalogs.length === 0 ? (
              <div className="admin-empty">{t("admin.catalogs.empty")}</div>
            ) : (
              catalogs.map((catalog) => (
                <article key={catalog.id} className="admin-catalog-card">
                  <div className="admin-catalog-info">
                    <h3>{catalog.title}</h3>

                    <p className="admin-catalog-file">{catalog.fileName}</p>

                    <div className="admin-catalog-meta">
                      <span className="admin-catalog-badge">
                        {t("admin.catalogs.downloads")}: {catalog.downloads ?? 0}
                      </span>

                      <span className="admin-catalog-date">
                        {t("admin.catalogs.lastDownload")}:{" "}
                        {catalog.lastDownloadAt
                          ? new Date(catalog.lastDownloadAt).toLocaleString()
                          : t("admin.catalogs.neverDownloaded")}
                      </span>
                    </div>
                  </div>

                  <div className="admin-catalog-actions">
                    <a href={catalog.fileUrl} target="_blank" rel="noreferrer">
                      {t("admin.catalogs.view")}
                    </a>

                    <button
                      type="button"
                      className="admin-catalog-copy-btn"
                      onClick={() => {
                        const url = `${window.location.origin}/api/catalogs/${catalog.id}/download`;
                        navigator.clipboard.writeText(url);
                        alert(t("admin.catalogs.linkCopied"));
                      }}
                    >
                      {t("admin.catalogs.copyLink")}
                    </button>

                    <button
                      type="button"
                      className="danger-btn"
                      onClick={() => deleteCatalog(catalog.id)}
                    >
                      {t("admin.catalogs.delete")}
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>
        </section>
      </section>
    </main>
  );
}